import Decimal from 'decimal.js'

import type { TPeriodicidadePrice } from '@/features/simulador/brasil/domain/gerenciador-intermediarias-price'
import type { TPeriodicidadePagamentoEnum } from '@/types/simulacao'
import { extractDateOnly } from '@/utils/date.utils'
import { roundFloat } from '@/utils/number.utils'

type TParcelaApi = { data: string; valor: number }
type TGrupoMensaisApi = { percentualParticipacao?: number | null; parcelas: TParcelaApi[] }
type TGrupoIntermediariasApi = {
  percentualParticipacao?: number | null
  periodicidade: TPeriodicidadePagamentoEnum
  parcelas: TParcelaApi[]
}

export class GruposParcelasApiPriceMapper {
  constructor(
    private readonly simulacao: {
      entrada: TParcelaApi[]
      mensais: TGrupoMensaisApi[]
      intermediarias: TGrupoIntermediariasApi[]
    },
    private readonly valorProposto: number,
  ) {}

  public buildGruposParcelas() {
    return {
      entradas: this.mapEntradas(),
      mensais: this.mapMensais(),
      intermediarias: this.mapIntermedarias(),
    }
  }

  private mapEntradas() {
    const grupos = this.simulacao.entrada.map((p) => ({ data: extractDateOnly(p.data), valor: p.valor }))
    if (!grupos.length) return

    const valorTotal = grupos.reduce((acc, g) => new Decimal(acc).plus(g.valor).toNumber(), 0)
    const percentual = roundFloat(new Decimal(valorTotal).mul(100).div(this.valorProposto).toNumber(), 2)

    return {
      formaDePagamento: grupos.length.toString() as '1' | '2' | '3' | '4' | '',
      percentual,
      valorTotal,
      grupos,
    }
  }

  private mapMensais() {
    const grupo = this.simulacao.mensais[0]
    if (!grupo?.parcelas?.length) return undefined

    const parcelasOrdenadas = [...grupo.parcelas].sort((a, b) => a.data.localeCompare(b.data))
    const valor = parcelasOrdenadas[0]!.valor
    const quantidade = parcelasOrdenadas.length
    const percentual = grupo.percentualParticipacao ?? 0
    const valorTotal = roundFloat(new Decimal(percentual).div(100).mul(this.valorProposto).toNumber(), 2)

    return {
      percentual,
      valorTotal,
      parcelas: [
        {
          valor,
          quantidade,
          dataInicio: extractDateOnly(parcelasOrdenadas[0]!.data),
          dataFim: extractDateOnly(parcelasOrdenadas.at(-1)!.data),
        },
      ],
    }
  }

  private mapIntermedarias() {
    const grupo = this.simulacao.intermediarias[0]
    if (!grupo?.parcelas?.length) return undefined

    const valor = grupo.parcelas[0]!.valor
    const parcelas = grupo.parcelas.map((p) => ({ data: extractDateOnly(p.data) }))
    const percentual = grupo.percentualParticipacao ?? 0
    const valorTotal = roundFloat(new Decimal(percentual).div(100).mul(this.valorProposto).toNumber(), 2)

    return {
      percentual,
      valorTotal,
      grupos: [
        { valor, quantidade: parcelas.length, periodicidade: grupo.periodicidade as TPeriodicidadePrice, parcelas },
      ],
    }
  }
}
